import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import firebase from 'firebase/app';
import dayjs from 'dayjs';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';

import DateRangePickerField from '../../components/DateRangePickerField';
import EnhancedTableHead from '../../components/EnhancedTableHead';
import SimpleTableToolbar from '../../components/SimpleTableToolbar';
import EnhancedTableRow from '../../components/EnhancedTableRow';
import { getComparator, stableSort } from '../../modules/sort';
import ExpandButton from '../../components/ExpandButton';
import SearchBox from '../../components/SearchBox';
import FabAdd from '../../components/FabAdd';
import ArrangeStockFormView from './ArrangeStockFormView';
import { useFirestoreDataAndMapping, unwrap } from '../../modules/uitls';
import ContextStore from '../../modules/context';

function ArrangeStockFormDetail({ userMapping, arrangeStockForm, ...props }) {
  const r = { ...arrangeStockForm }
  const keys = arrangeStockForm.merchandises ? Object.keys(arrangeStockForm.merchandises) : []
  r.merchandises = keys.map(k => {
    return { ...r.merchandises[k], id: k }
  })

  r.createdBy = { id: r.createdBy, name: userMapping[r.createdBy]?.displayName }
  return (
    <div style={{ padding: 15 }}>
      <ArrangeStockFormView userMapping={userMapping} arrangeStockForm={r} {...props} />
    </div>
  )
}

ArrangeStockFormDetail.propTypes = {
  arrangeStockForm: PropTypes.object.isRequired,
  userMapping: PropTypes.object.isRequired,
  merchandiseMapping: PropTypes.object.isRequired,
};

function ArrangeStockFormList() {
  const { formatMessage } = useIntl()
  const { setBreadcrumbs, currentUser } = useContext(ContextStore)
  const location = useLocation()
  const wareroom = useSelector(state => unwrap(state.firestore.data.config?.wareroom || {}))

  const [userMapping] = useFirestoreDataAndMapping('users')
  const [customerMapping] = useFirestoreDataAndMapping('customers')
  const [merchandiseMapping] = useFirestoreDataAndMapping('merchandises')

  const [order, setOrder] = useState('desc')
  const [orderBy, setOrderBy] = useState('sn')
  const [currentFilter, setCurrentFilter] = useState(null)
  const [expand, setExpand] = useState(true);
  const [arrangeStockForms, setArrangeStockForms] = useState([])
  const [startDate, setStartDate] = useState(dayjs().subtract(2, 'months').format('YYYY-MM') + '-01')
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'))

  useEffect(() => {
    setBreadcrumbs([{
      text: formatMessage({ id: 'sideMenu.stock.arrangeStockForm' })
    }])
    return () => {
    };
  }, [location.pathname]);

  useEffect(() => {
    if (arrangeStockForms.length) setArrangeStockForms([])
    const onSnapshot = snapshot => {
      const arrangeStockForms = []
      snapshot.forEach(doc => {
        arrangeStockForms.push({ ...doc.data(), id: doc.id })
      });
      setArrangeStockForms(arrangeStockForms)
    }
    const unsubscribe = firebase.firestore().collection('arrangeStockForms').where('date', '>=', startDate).where('date', '<=', endDate).onSnapshot(onSnapshot, err => {})
    return () => unsubscribe()
  }, [startDate, endDate]);

  const arrangeStockFormsForTab = arrangeStockForms.filter(r =>
    // TODO: 調撥單也需要一組權限
    currentUser.userRights.debugging || currentUser.deliveryOrderSource.includes(r.source)
  );

  const filteredArrangeStockForms = filterArrangeStockForms()

  const headerCells = [
    { text: 'sn', sort: 'sn' },
    { text: 'sourceName', sort: 'sourceName' },
    { text: 'createdBy', sort: 'createdBy' },
    { text: 'date', sort: 'date' },
  ].map(c => {c.text = formatMessage({ id: `arrangeStockForm.table.header.${c.text}` });return c})

  const rowCells = [
    { field: 'sn' },
    { field: 'sourceName' },
    { field: 'createdBy' },
    { field: 'date' },
  ]

  function checkUserRight() {
    // 目前 調撥單 和 銷貨單 共用權限
    return currentUser.userRights['deliveryOrder-create']
  }

  function filterArrangeStockForms() {
    let items = [...arrangeStockFormsForTab]

    if (currentFilter && currentFilter.text) {
      const lowerCaseText = currentFilter.text.toLowerCase()
      if (currentFilter.name === 'sourceName') {
        return items.filter(s => customerMapping[s.source]?.nickname.toLowerCase().includes(lowerCaseText))
      } else if (currentFilter.name === 'createdBy') {
        return items.filter(s => userMapping[s.createdBy]?.displayName.toLowerCase().includes(lowerCaseText))
      } else if (currentFilter.name === 'merchandiseName') {
        return items.filter(s => Object.keys(s.merchandises || {}).filter(c => merchandiseMapping[c])
          .filter(c => merchandiseMapping[c].name.toLowerCase().includes(lowerCaseText) ||
            merchandiseMapping[c].nickname.toLowerCase().includes(lowerCaseText)).length)
      } else if (currentFilter.name === 'merchandiseCode') {
        return items.filter(s => Object.keys(s.merchandises || {}).filter(c => merchandiseMapping[c])
          .filter(c => merchandiseMapping[c].code.toLowerCase().includes(lowerCaseText)).length)
      } else {
        return items.filter(s => s[currentFilter.name].toLowerCase().includes(lowerCaseText))
      }
    }
    return items
  }

  const formatData = (arrangeStockForms) => {
    const newData = { ...arrangeStockForms }
    newData.sourceName = customerMapping[newData.source]?.nickname
    newData.createdBy = userMapping[newData.createdBy]?.displayName
    return newData
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filterItems = [
    { name: 'sn' },
    { name: 'sourceName', type: 'customer' },
    { name: 'createdBy' },
    { name: 'merchandiseName' },
    { name: 'merchandiseCode' },
    { name: 'note' },
  ].map(i => {i.text = formatMessage({ id: `arrangeStockForm.table.detail.${i.name}` });return i})

  const onFilterChanged = (name, text) => {
    if (text !== '') {
      setCurrentFilter({ name, text })
    } else {
      setCurrentFilter(null)
    }
  }

  function getToolbox() {
    return <SearchBox filterItems={filterItems} onFilterChanged={onFilterChanged} />
  }

  return (
    <div style={{ padding: '24px 24px 60px 24px' }}>
      {checkUserRight() === true && <FabAdd to="/stock/arrangeStockForm/edit/new" />}
      <DateRangePickerField
        startDate={startDate}
        endDate={endDate}
        onStartDateChanged={setStartDate}
        onEndDateChanged={setEndDate}
      />
      <SimpleTableToolbar
        title={'arrangeStockForm.table.title'}
        buttons={<ExpandButton open={expand} onExpandChange={setExpand} />}
        toolbox={getToolbox()}
      />
      <Collapse in={expand} timeout="auto" unmountOnExit>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: '650px' }}>
            <EnhancedTableHead
              headerCells={headerCells}
              order={order}
              orderBy={orderBy}
              expandable
              onRequestSort={handleRequestSort}
              rowCount={filteredArrangeStockForms.length}
            />
            <TableBody>
              {stableSort(filteredArrangeStockForms.map(r => formatData(r)), getComparator(order, orderBy)).map(arrangeStockForm => (
                <EnhancedTableRow
                  key={arrangeStockForm.id}
                  rowCells={rowCells}
                  cellData={arrangeStockForm}
                  expandable
                  expandContent={<ArrangeStockFormDetail
                    userMapping={userMapping}
                    merchandiseMapping={merchandiseMapping}
                    arrangeStockForm={arrangeStockForm}
                    wareroom={wareroom}
                  />}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </div>
  );
}

export default ArrangeStockFormList;
